import React, { useState, useEffect, useRef } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { useSessionStateStore } from '@renderer/stores/sessionState'
import { useConfigStore } from '@renderer/stores/configState'
import { useCountdown } from '@renderer/utils/countdownUtils'
import { useInterval } from '@renderer/utils/useInterval'
import { takeSessionId } from '@renderer/utils/sessionIdSource'
import cogoToast from '@successtar/cogo-toast'
import Preview from '@renderer/components/Preview'
import Spinner from '@renderer/components/Spinner'
import QrCodeImage from '@renderer/components/QrCodeImage'
import MidtransUtils from '@renderer/utils/midtransUtils'
import { useCachedImage } from '@renderer/hooks/useCachedImage'
import { useCachedBackgroundImage } from '@renderer/hooks/useCachedBackgroundImage'
import { RecordingType } from '@renderer/types/configState'
import { formatAmount, normalizeCurrency } from '@renderer/utils/formatCurrency'
import { useProjectLanguages } from '@renderer/hooks'
import { useCameraStore } from '@renderer/stores/cameraStore'
import { ensureCameraReadyForStart } from '@renderer/utils/cameras/cameraStartGate'

// Bahela assets
import bgLayer from '/assets/bahela/bg-layer.webp'
import bahelaLogo from '/assets/bahela/bahela.webp'
import arrowWhite from '/assets/bahela/arrow-white.svg'
import qrisId from '/assets/bahela/qris-holder-id.webp'
import qrisEn from '/assets/bahela/qris-holder-en.webp'
import { qrActivityLogger } from '@renderer/utils/qrLoadReporter'

// bahela-quick merges Welcome + Payment into one page and is re-entered directly
// after every completed session (Result navigates straight back here). That makes
// clean per-session state and a self-healing QR the whole ballgame: any stale
// transaction or a single flaky payment call used to strand the operator on a
// blank QR, which is why session-end previously did a full-page reload (which
// killed the live camera). We instead (a) reset every payment field on mount and
// (b) auto-retry a failed payment init, so the QR reliably reappears — no reload.
const MAX_PAYMENT_RETRIES = 5
const PAYMENT_RETRY_DELAY_MS = 3000

const Welcome: React.FC = () => {
  const { i18n } = useTranslation()
  const navigate = useNavigate()
  const { projectId } = useParams()
  const { config: globalConfig, getPageSettings } = useConfigStore()
  const projectCurrency = normalizeCurrency(globalConfig?.resolvedCurrency)
  // The languages this project offers (ID/EN for IDR, ES/EN for
  // Spanish-speaking markets, English-only elsewhere — enforced by
  // `useEnforceProjectLanguage` in ProjectRoute). One language means no
  // toggle.
  const languageOptions = useProjectLanguages()
  const showLanguageToggle = languageOptions.length > 1
  const {
    setSessionState,
    uploadSessionState,
    logActivity,
    transaction,
    userPreferences
  } = useSessionStateStore()
  const { config, filters, frames } = useConfigStore()
  const isMounted = useRef(true)

  const [qrUrl, setQrUrl] = useState<string | null>(null)
  const [remainingTime, setRemainingTime] = useState<string | null>(null)
  const [isExpired, setIsExpired] = useState(false)
  const [isCheckingPayment, setIsCheckingPayment] = useState(false)
  const [paymentStatus, setPaymentStatus] = useState<'pending' | 'success' | 'failed'>('pending')
  const [activeFilterIndex, setActiveFilterIndex] = useState(0)
  const [logoClickCount, setLogoClickCount] = useState(0)
  const logoClickTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  const navigateTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  const paymentRetryTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  const captureFallbackTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  const paymentAttemptRef = useRef(0)
  // Latched the instant payment settles (paid or operator-skipped). Once set, NO
  // payment timer may run again this session — this is what kills the post-success
  // expiry-countdown leak that could fire behind the success/redirect screen.
  const paymentSettledRef = useRef(false)
  // Mount-scoped session identity + the last transaction we issued. Defense-in-depth
  // for the store-wipe class of bugs (a trailing cleanupSession from the PREVIOUS
  // session resetting the store while our QR is live — guarded at the store level
  // too). Without these, a wiped transaction makes checkPaymentStatus no-op forever:
  // the guest pays a QR the booth can never confirm, then gets bounced back to the
  // QR ("sudah bayar tapi balik ke beranda"). With them, the poll detects the loss,
  // restores id + transaction, and settlement detection keeps working.
  const sessionIdRef = useRef('')
  const liveTxRef = useRef<{
    orderId: string
    qrUrl: string
    expiryTime: string
    serverTime: string
    status: 'pending'
    amount: number
    discount: number
  } | null>(null)
  const [previewKey, setPreviewKey] = useState(0)

  const paymentSettings = getPageSettings('payment')
  const paymentCountdown = useCountdown(paymentSettings?.paymentTimeout || 120)

  const safeUpdate = (callback: () => void): void => {
    if (isMounted.current) callback()
  }

  // Unmanned-booth camera gate. This page merges Welcome+Payment, so by the time we
  // navigate to capture the guest may have ALREADY PAID — a failed gate must not
  // strand them. On failure: surface the CameraPrompt (shared gate dispatches it)
  // and remember the pending navigation; the effect below completes it the moment a
  // camera connects (operator taps the prompt → reconnect → session continues).
  const [pendingCaptureNavigate, setPendingCaptureNavigate] = useState(false)
  const { camera: liveCamera, webcam: liveWebcam } = useCameraStore()

  // How long to wait for a camera after a PAID guest hits a failed start-gate
  // before proceeding to capture anyway. Capture owns camera recovery + the
  // operator hold-prompt, so landing there is always better than leaving a paid
  // guest stuck on the "redirecting…" screen.
  const CAPTURE_GATE_FALLBACK_MS = 20000

  const clearCaptureFallback = (): void => {
    if (captureFallbackTimerRef.current) {
      clearTimeout(captureFallbackTimerRef.current)
      captureFallbackTimerRef.current = null
    }
  }

  const proceedToCapture = async (): Promise<void> => {
    if (await ensureCameraReadyForStart()) {
      clearCaptureFallback()
      navigate(`/${projectId}/capture`)
      return
    }
    // Camera not ready. Defer, but NEVER strand a paid guest: the effect below
    // completes the moment a camera connects, and this bounded fallback forces the
    // navigation even if that store event never arrives.
    setPendingCaptureNavigate(true)
    clearCaptureFallback()
    captureFallbackTimerRef.current = setTimeout(() => {
      captureFallbackTimerRef.current = null
      if (isMounted.current) {
        console.warn('[BAHELA-QUICK] Camera-gate fallback — forcing capture nav for paid guest')
        navigate(`/${projectId}/capture`)
      }
    }, CAPTURE_GATE_FALLBACK_MS)
  }

  useEffect(() => {
    if (!pendingCaptureNavigate || !(liveCamera || liveWebcam)) return
    setPendingCaptureNavigate(false)
    clearCaptureFallback()
    console.log('[BAHELA-QUICK] Camera back — completing deferred capture navigation')
    navigate(`/${projectId}/capture`)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pendingCaptureNavigate, liveCamera, liveWebcam])

  const clearPaymentRetry = (): void => {
    if (paymentRetryTimerRef.current) {
      clearTimeout(paymentRetryTimerRef.current)
      paymentRetryTimerRef.current = null
    }
  }

  // Payment is done for this session (paid or operator-skipped). Stop EVERY payment
  // timer and latch the settled flag so a leftover expiry countdown / status poll /
  // retry can never fire behind the success screen or after we navigate on — the
  // classic post-success timeout leak that bounced paid guests back to the QR.
  const settlePayment = (): void => {
    paymentSettledRef.current = true
    paymentCountdown.stop()
    clearPaymentRetry()
    setIsCheckingPayment(false)
    setRemainingTime(null)
  }

  // Cancel the pending order on the gateway. Reads the LIVE store (not a render
  // closure) so it stays correct when called from unmount / countdown-retry after
  // the transaction has changed since the last render.
  const cleanupPayment = async (): Promise<void> => {
    const tx = useSessionStateStore.getState().transaction
    if (tx?.orderId && tx?.status !== 'settlement') {
      try {
        await fetch(`${process.env.VITE_API_URL}/api/payments/${tx.orderId}`, {
          method: 'DELETE',
          credentials: 'include'
        })
      } catch (error) {
        console.error('Error cancelling payment:', error)
      }
    }
  }

  const calculateRemainingTime = (expiryTime: string, serverTime: string): number => {
    try {
      const expiryTimestamp = new Date(expiryTime).getTime()
      const serverTimestamp = new Date(serverTime).getTime()
      return Math.max(0, Math.floor((expiryTimestamp - serverTimestamp) / 1000))
    } catch {
      return 0
    }
  }

  const formatTime = (seconds: number): string => {
    const m = Math.floor(seconds / 60)
    const s = seconds % 60
    return `${m.toString().padStart(2, '0')}:${s.toString().padStart(2, '0')}`
  }

  // Default per-session price. Uses the checkout "print" package when configured,
  // otherwise the 25k floor (keraton has no checkout packages, so it resolves to 25k).
  const getDefaultAmount = (): number =>
    config?.pages?.checkout?.settings?.packages?.find(
      (p: { type: string }) => p.type === 'print'
    )?.price ?? 25000

  // Number of printed copies per session. The quick flow has no checkout UI, so copies
  // must be stamped up front from the configured "print" package quantity (default 1 —
  // keraton/cikstik have no checkout packages, so they resolve to a single strip). Without
  // this, userPreferences.copies stays at the store default (0) all the way to the Frame
  // print call, which sent copies:0 to the printer (masked only by the main-process
  // `copies || 1` fallback, so a quick booth could never print more than one copy).
  const getDefaultCopies = (): number =>
    config?.pages?.checkout?.settings?.packages?.find(
      (p: { type: string; quantity?: number }) => p.type === 'print'
    )?.quantity ?? 1

  // Start a brand-new session on every Welcome mount. This is the single
  // authoritative write that clears ALL state from the previous customer:
  // a fresh id, an empty gallery, and a fully-reset transaction (no stale
  // orderId / qrUrl / settlement status leaking into the new payment). Returns
  // the new session id so the payment call uses it directly instead of a stale
  // render closure.
  const startFreshSession = (): string => {
    const newId = takeSessionId()
    sessionIdRef.current = newId
    liveTxRef.current = null
    const recordingType = config?.pages?.checkout?.settings?.packages?.find(
      (p: { type: string }) =>
        p.type === 'recording' ||
        p.type === 'strip_live' ||
        p.type === 'strip_story' ||
        p.type === 'session_recording'
    )?.type as RecordingType

    // Reset local payment UI to a clean slate for the new customer.
    clearPaymentRetry()
    clearCaptureFallback()
    paymentAttemptRef.current = 0
    paymentSettledRef.current = false
    setPendingCaptureNavigate(false)
    paymentCountdown.stop()
    setQrUrl(null)
    setIsExpired(false)
    setIsCheckingPayment(false)
    setPaymentStatus('pending')
    setRemainingTime(null)

    setSessionState({
      id: newId,
      projectId,
      mode: 'capture',
      timeline: { startTime: new Date() },
      userPreferences: {
        filterId: filters[activeFilterIndex]?._id ?? filters[0]?._id,
        frameId: frames[0]?._id,
        copies: getDefaultCopies(),
        useGIF: getPageSettings('capture')?.gifEnabled,
        useRecording: getPageSettings('capture')?.recordingEnabled,
        useVideo: getPageSettings('record')?.enabled,
        recordingType
      },
      gallery: { id: `${9999999999999 - Date.now()}-${newId}`, capturedItems: [] },
      transaction: {
        orderId: null,
        qrUrl: null,
        expiryTime: null,
        serverTime: null,
        status: 'pending',
        amount: getDefaultAmount(),
        discount: 0
      }
    })
    uploadSessionState()
    return newId
  }

  const schedulePaymentRetry = (sessionId: string, errMsg: string): void => {
    if (!isMounted.current || paymentSettledRef.current) return
    if (paymentAttemptRef.current >= MAX_PAYMENT_RETRIES) {
      logActivity('Payment initiation failed — retries exhausted', {
        error: errMsg,
        level: 'error'
      })
      safeUpdate(() => cogoToast.error(errMsg))
      return
    }
    paymentAttemptRef.current += 1
    logActivity('Payment initiation failed — retrying', {
      error: errMsg,
      attempt: paymentAttemptRef.current
    })
    clearPaymentRetry()
    paymentRetryTimerRef.current = setTimeout(() => {
      if (isMounted.current) void initiatePayment(sessionId)
    }, PAYMENT_RETRY_DELAY_MS)
  }

  const initiatePayment = async (sessionIdParam?: string): Promise<void> => {
    if (!isMounted.current || paymentSettledRef.current) return
    const state = useSessionStateStore.getState()
    const sessionId = sessionIdParam || state.id || ''
    const finalAmount =
      state.transaction?.amount != null && state.transaction.amount > 0
        ? state.transaction.amount
        : getDefaultAmount()
    try {
      const data = await MidtransUtils.initiatePayment(
        finalAmount,
        sessionId,
        paymentSettings?.paymentTimeout || 120,
        projectId || '',
        state.userPreferences?.filterId || filters[0]?._id || '',
        state.userPreferences?.frameId || frames[0]?._id || '',
        state.transaction?.discount ?? 0
      )

      // A payment that settled while this request was in flight must win — never
      // arm a fresh expiry countdown on top of a completed payment.
      if (!isMounted.current || paymentSettledRef.current) return

      if (data.order_id && data.qr_url) {
        paymentAttemptRef.current = 0
        const sanitizedQrUrl = data.qr_url.replace(/([^:]\/)\/+/g, '$1')
        const initialRemaining = calculateRemainingTime(data.expiry_time, data.server_time)
        if (initialRemaining <= 0) {
          safeUpdate(() => setIsExpired(true))
          return
        }

        const issuedTx = {
          orderId: data.order_id,
          qrUrl: sanitizedQrUrl,
          expiryTime: data.expiry_time,
          serverTime: data.server_time,
          status: 'pending' as const,
          amount: finalAmount,
          discount: state.transaction?.discount ?? 0
        }
        liveTxRef.current = issuedTx
        setSessionState({ transaction: issuedTx })
        logActivity('Payment process started', {
          amount: finalAmount,
          orderId: data.order_id
        })

        safeUpdate(() => {
          setQrUrl(sanitizedQrUrl)
          setIsExpired(false)
          paymentCountdown.setRemainingSeconds(initialRemaining)
          paymentCountdown.setOnTick(() => {
            setRemainingTime(formatTime(paymentCountdown.getRemainingSeconds()))
          })
          paymentCountdown.setOnComplete(handleCountdownEnd)
          paymentCountdown.start()
          setIsCheckingPayment(true)
        })
      } else {
        throw new Error('Payment response missing order_id or qr_url')
      }
    } catch (e: unknown) {
      console.error('Error initiating payment:', e)
      // Self-heal a transient failure (network / gateway blip) so the QR
      // reappears on its own — no full-page reload required.
      schedulePaymentRetry(sessionId, (e as Error).message)
    }
  }

  const handleCountdownEnd = async (): Promise<void> => {
    // A leaked/late-firing expiry countdown must be a no-op once payment settled.
    if (!isMounted.current || paymentSettledRef.current) return
    await checkPaymentStatus()
    // Paid right at the buzzer? The success path already settled + scheduled
    // navigation — don't clobber it with an expiry or a QR regeneration.
    if (paymentSettledRef.current || useSessionStateStore.getState().transaction?.status === 'settlement')
      return

    safeUpdate(() => {
      setIsCheckingPayment(false)
      setRemainingTime('00:00')
      setIsExpired(true)
    })
    logActivity('Payment countdown ended — regenerating QR', {
      orderId: useSessionStateStore.getState().transaction?.orderId
    })

    // Auto-retry: cancel the expired order, clear the transaction, then issue a
    // fresh QR for the same session so a waiting customer isn't stuck on "expired".
    await cleanupPayment()
    const sessionId = useSessionStateStore.getState().id || ''
    safeUpdate(() => {
      setQrUrl(null)
      liveTxRef.current = null
      setSessionState({
        transaction: {
          orderId: null,
          qrUrl: null,
          expiryTime: null,
          serverTime: null,
          status: 'pending'
        }
      })
    })
    await new Promise((resolve) => setTimeout(resolve, 2500))
    if (!isMounted.current) return
    paymentAttemptRef.current = 0
    safeUpdate(() => setIsExpired(false))
    await initiatePayment(sessionId)
  }

  const checkPaymentStatus = async (isButtonClick = false): Promise<void> => {
    if (!isMounted.current || paymentSettledRef.current) return
    // Read the live transaction so an in-flight status check after a QR
    // regeneration always targets the current order.
    let tx = useSessionStateStore.getState().transaction
    // Self-repair: if the store lost OUR in-flight payment (id hijacked or
    // transaction blanked by a concurrent reset — the trailing-cleanup wipe),
    // restore it from the mount-scoped refs instead of silently no-oping while
    // a customer is mid-payment. We are the mounted Welcome (isMounted above),
    // so the store is rightfully ours.
    const storeId = useSessionStateStore.getState().id
    if (
      liveTxRef.current &&
      (storeId !== sessionIdRef.current || !tx?.expiryTime || !tx?.serverTime)
    ) {
      console.warn(
        '[BAHELA-QUICK] Session store lost the live payment (wiped/hijacked) — restoring id + transaction',
        { expected: sessionIdRef.current, found: storeId }
      )
      logActivity(
        'Payment transaction restored after store wipe',
        { orderId: liveTxRef.current.orderId, foundSessionId: storeId, level: 'error' },
        undefined,
        sessionIdRef.current
      )
      setSessionState({ id: sessionIdRef.current, transaction: liveTxRef.current })
      tx = useSessionStateStore.getState().transaction
    }
    if (!tx?.expiryTime || !tx?.serverTime) return
    try {
      const paymentData = await MidtransUtils.checkPaymentStatus(tx.orderId || 'order-default-id')
      if (paymentData.isExpired) {
        safeUpdate(() => {
          setIsCheckingPayment(false)
          setIsExpired(true)
          setSessionState({ transaction: { status: 'expire' } })
          uploadSessionState()
        })
        return
      }
      if (paymentData.paid) {
        safeUpdate(() => {
          // Settle FIRST: stop the expiry countdown + retry timers before we show
          // success and schedule navigation, so nothing payment-related can fire
          // during the redirect / camera-gate wait.
          settlePayment()
          setPaymentStatus('success')
          // Re-assert the session id in the same write: if anything blanked it while
          // the guest was paying, PageWrapper's `!sessionId` guard would bounce this
          // PAID guest from /capture straight back to the QR page.
          setSessionState({ id: sessionIdRef.current, transaction: { status: 'settlement' } })
          logActivity('Payment completed successfully', {
            orderId: tx.orderId,
            transactionStatus: paymentData.transactionStatus
          })
          uploadSessionState()
          const tid = setTimeout(() => {
            navigateTimeoutRef.current = null
            if (isMounted.current) void proceedToCapture()
          }, 2000)
          navigateTimeoutRef.current = tid
        })
      } else if (isButtonClick) {
        setPaymentStatus('failed')
        setTimeout(() => setPaymentStatus('pending'), 2000)
      }
    } catch (e: unknown) {
      console.error('Error checking payment status:', e)
      if (isButtonClick) safeUpdate(() => cogoToast.error((e as Error).message))
    }
  }

  const changeLanguage = (lng: string): void => {
    i18n.changeLanguage(lng)
    localStorage.setItem('language', lng)
  }

  const handleLogoClick = (): void => {
    if (logoClickTimeoutRef.current) {
      clearTimeout(logoClickTimeoutRef.current)
      logoClickTimeoutRef.current = null
    }
    const newCount = logoClickCount + 1
    setLogoClickCount(newCount)
    if (newCount >= 5) {
      setLogoClickCount(0)
      // Operator skip: settle payment (stops every payment timer) and mark the
      // (already-fresh) session paid.
      settlePayment()
      const sessionId = useSessionStateStore.getState().id || takeSessionId()
      setSessionState({ id: sessionId, transaction: { status: 'settlement' } })
      uploadSessionState()
      logActivity('Payment skipped via logo click (5x)')
      void proceedToCapture()
      return
    }
    logoClickTimeoutRef.current = setTimeout(() => setLogoClickCount(0), 2000)
  }

  const slidePrev = (): void => {
    if (filters.length === 0) return
    const next = activeFilterIndex <= 0 ? filters.length - 1 : activeFilterIndex - 1
    setActiveFilterIndex(next)
    const filter = filters[next]
    if (filter) {
      setSessionState({ userPreferences: { filterId: filter._id } })
      logActivity(`Selected filter: ${filter.name || filter._id}`, { filterId: filter._id })
    }
  }

  const slideNext = (): void => {
    if (filters.length === 0) return
    const next = (activeFilterIndex + 1) % filters.length
    setActiveFilterIndex(next)
    const filter = filters[next]
    if (filter) {
      setSessionState({ userPreferences: { filterId: filter._id } })
      logActivity(`Selected filter: ${filter.name || filter._id}`, { filterId: filter._id })
    }
  }

  useEffect(() => {
    isMounted.current = true
    // Fresh session + fresh QR on every mount (including SPA re-entry after a
    // completed session). One clean write, then initiate payment with the new id.
    const sessionId = startFreshSession()
    void initiatePayment(sessionId)
    setPreviewKey((k) => k + 1)
    return (): void => {
      isMounted.current = false
      paymentCountdown.stop()
      clearPaymentRetry()
      clearCaptureFallback()
      cleanupPayment()
      if (logoClickTimeoutRef.current) clearTimeout(logoClickTimeoutRef.current)
      if (navigateTimeoutRef.current) {
        clearTimeout(navigateTimeoutRef.current)
        navigateTimeoutRef.current = null
      }
    }
  }, [])

  useInterval(() => checkPaymentStatus(), isCheckingPayment ? 5000 : null)

  useEffect(() => {
    const filter = filters[activeFilterIndex]
    if (filter) setSessionState({ userPreferences: { filterId: filter._id } })
  }, [activeFilterIndex])

  // Key by filter id so Preview remounts on filter change (same as view/Filter.tsx) - avoids flicker
  // from Preview's effect re-running when filter prop changes
  const selectedFilterId = filters[activeFilterIndex]?._id ?? ''

  const cachedBgLayer = useCachedBackgroundImage(bgLayer)
  const cachedBahelaLogo = useCachedImage(bahelaLogo)
  const cachedArrowWhite = useCachedImage(arrowWhite)
  const cachedQris = useCachedImage(i18n.language === 'id' ? qrisId : qrisEn)

  if (paymentStatus === 'success') {
    return (
      <div className="background relative bg-prominentBlue w-full h-full overflow-hidden flex items-center justify-center">
        <div className="text-uriYellow font-serif-bahela text-5xl">
          {i18n.language === 'id' ? 'Pembayaran berhasil! Mengalihkan...' : 'Payment successful! Redirecting...'}
        </div>
      </div>
    )
  }

  return (
    <div className="background relative bg-prominentBlue w-full h-full overflow-hidden">
      <div
        className="deco-img absolute top-0 left-0 w-full h-full bg-cover z-[0]"
        style={{ backgroundImage: `url(${cachedBgLayer})` }}
      />

      {/* Language toggle - top left. Hidden on non-IDR projects. */}
      {showLanguageToggle && (
        <div className="z-[10] border-[7px] inline-flex drop-shadow-xl items-center border-uriYellow rounded-full text-[36px] p-1 font-sans font-bold absolute top-24 left-20">
          {languageOptions.map((lng) => (
            <button
              key={lng}
              type="button"
              className={`px-4 py-2 rounded-full w-[77px] h-[56px] flex justify-center items-center ${
                i18n.language === lng ? 'bg-uriYellow text-hibiscus' : 'text-uriYellow'
              }`}
              onClick={() => changeLanguage(lng)}
            >
              {lng.toUpperCase()}
            </button>
          ))}
        </div>
      )}

      {/* Yellow logo - top right; 5x click skips payment */}
      <button
        type="button"
        onClick={handleLogoClick}
        className="z-[10] absolute top-20 right-20 p-0 border-0 bg-transparent cursor-pointer"
        aria-label="Bahela logo"
      >
        <img
          src={cachedBahelaLogo}
          alt="Bahela"
          className="object-contain w-32 h-32"
        />
      </button>

      {/* Main: Preview (left) + Payment container (right) - larger boxes */}
      <div className="absolute inset-0 flex items-center justify-center gap-10 pt-24 pb-32 px-16 z-[5]">
        {/* Left: Preview - larger */}
        <div className="flex-shrink-0 w-[540px] flex flex-col items-center">
          <div className="w-full rounded-2xl border-[6px] border-uriYellow bg-black/30 overflow-hidden">
            <div className="aspect-square w-full min-h-[380px] relative bg-gray-800">
              <Preview
                key={`${selectedFilterId}-${previewKey}`}
                adjustments={filters[activeFilterIndex]?.adjustments}
                filter={selectedFilterId}
                postLUTAdjustments={filters[activeFilterIndex]?.postLUTAdjustments}
                ratio={1}
                cameraKey={userPreferences?.selectedCamera as 'primary' | 'secondary'}
              />
            </div>
          </div>
        </div>

        {/* Right: Payment - QRIS holder (larger); Rp + QR aligned to top */}
        <div className="flex-1 flex flex-col items-center max-w-[520px]">
          <div className="relative w-full aspect-square max-w-[500px]">
            <img
              src={cachedQris}
              alt="QRIS Holder"
              className="w-full h-full object-contain"
            />
            <div className="absolute inset-0 flex flex-col items-center pt-[10%]">
              <h2 className="font-serif-bahela text-methadone drop-shadow-2xl text-3xl md:text-4xl mb-2">
                {formatAmount(
                  (transaction?.amount != null && transaction.amount > 0)
                    ? transaction.amount
                    : getDefaultAmount(),
                  projectCurrency
                )}
              </h2>
              {isExpired ? (
                <div className="flex flex-col items-center justify-center gap-4 mt-2">
                  <p className="text-xl font-serif-bahela text-methadone">
                    {i18n.language === 'id' ? 'Kode kedaluwarsa' : 'Code expired'}
                  </p>
                </div>
              ) : qrUrl ? (
                <>
                  {/* Robust QR renderer: spinner until the image actually loads,
                      never flashes alt text, auto-retries a flaky fetch. */}
                  <div className="w-[52%] max-w-[280px] aspect-square">
                    <QrCodeImage
                      qrUrl={qrUrl}
                      className="w-full h-full object-contain"
                      onEvent={qrActivityLogger(logActivity, 'bahela-quick-payment')}
                    />
                  </div>
                  <span className="text-[28px] text-white font-serif-bahela mt-2 drop-shadow-2xl">
                    {remainingTime ?? '--:--'}
                  </span>
                </>
              ) : (
                <div className="flex flex-col items-center gap-4 mt-4">
                  <Spinner />
                  <span className="text-white text-lg">Loading QR...</span>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Filter carousel - below main; larger options, arrow-white.svg, correct left/right */}
      {filters.length > 0 && (
      <div className="absolute bottom-32 left-0 right-0 flex items-center justify-center gap-6 z-[5]">
        <button
          type="button"
          className="w-20 h-20 rounded-full border-2 border-uriYellow flex items-center justify-center text-white hover:bg-uriYellow/20 transition-colors"
          onClick={slidePrev}
          aria-label="Previous filter"
        >
          <img src={cachedArrowWhite} className="w-12 h-12 object-contain" alt="" />
        </button>
        <div className="flex gap-6 items-center">
          {filters.map((filter, index) => (
            <button
              key={filter._id}
              type="button"
              onClick={() => {
                setActiveFilterIndex(index)
                setSessionState({ userPreferences: { filterId: filter._id } })
              }}
              className={`rounded-full px-10 py-5 border-[4px] border-uriYellow font-serif-bahela text-3xl md:text-4xl transition-all ${
                activeFilterIndex === index
                  ? 'bg-uriYellow text-hibiscus scale-110'
                  : 'bg-uriYellow/20 text-uriYellow'
              }`}
            >
              {filter.name}
            </button>
          ))}
        </div>
        <button
          type="button"
          className="w-20 h-20 rounded-full border-2 border-uriYellow flex items-center justify-center text-white hover:bg-uriYellow/20 transition-colors"
          onClick={slideNext}
          aria-label="Next filter"
        >
          <img src={cachedArrowWhite} className="w-12 h-12 object-contain scale-x-[-1]" alt="" />
        </button>
      </div>
      )}

      {/* Rolling text - bottom */}
      <div className="marquee-container bottom-marquee tracking-[0.28em] z-[2]">
        <div className="marquee-content">
          <span className="font-body-bahela text-uriYellow inline-block text-[38px] italic">
            {i18n.language === 'id'
              ? 'BAHeLA bilik foto lawas untuk menangkap kenangan indahmu bersama orang terkasih di hidupmu'
              : 'BAHeLA vintage photobooth captures beautiful memories with your loved ones'}
          </span>
        </div>
      </div>
    </div>
  )
}

export default Welcome
